import { useFieldArray, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
	Checkbox,
	CircularProgress,
	IconButton,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	// TextField,
} from '@mui/material';
import { useDropzone } from 'react-dropzone';
import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { twMerge } from 'tailwind-merge';
import { Add, Circle, CircleOutlined, Close, Delete } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactPlayer from 'react-player';

import BaseInputValidation from '@/components/ui/Input/BaseInputValidation';
import BaseAutocomplete from '@/components/ui/Select/BaseAutocomplete';
import BaseSelect from '@/components/ui/Select/BaseSelect';
import ProjectRelation from '@/components/Modal/Project/ProjectRelation';
import Loader from '@/components/ui/Loader/Loader';
import BDHelmet from '@/components/BDHelmet';
import FileExist from '@/components/Modal/Project/FileExist';
import Reference from '@/components/ui/Reference/Reference';

import { checkFileExist } from '@/helper/checkFileExist';
import { uploadBlob } from '@/helper/uploadBlob';
import usePatch from '@/hooks/usePatch';
import useVideoUrl from '@/hooks/useVideoUrl';
import projectApi from '@/api/project';

import { route } from '@/constants/routeName';

const { createProject: cP, getMaxCodeNumber, getProductGroup, getProductCategory, getProduct } = projectApi();

const schema = yup.object().shape({
	productprefixid: yup.object().required(),
	productcodenumber: yup.string().required(),
	productname: yup.string().required(),
	category: yup.string(),
	producttype: yup.string().required(),
	status: yup.string().required(),
});

export default function Create() {
	const navigate = useNavigate();
	const productCategory = useSelector((state) => state.content.productCategory);
	const productPrefix = useSelector((state) => state.content.productPrefix);
	const projectStatus = useSelector((state) => state.content.projectStatusD);

	const [tb, setTb] = useState(false);
	const [openRelation, setOpenRelation] = useState(false);
	const [filesAttachment, setFilesAttachment] = useState([]);
	const [projectRelations, setProjectRelations] = useState([]);
	const [isFileExist, setIsFileExist] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isChecked, setIsChecked] = useState(false);

	const {
		handleSubmit,
		control,
		setValue,
		watch,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			status: 9,
			qrstatus: '0',
			productversion: '',
			productcomponentflag: 0,
			description: '',
		},
		reValidateMode: 'onChange',
	});

	const dType = watch('productprefixid');
	const number = watch('productcodenumber');
	const ver = watch('productversion');
	const vUrl = watch('producturl');
	const isVideo = useMemo(() => dType?.productprefix.toLowerCase().includes('video'), [dType]);

	const { videoUrl } = useVideoUrl(dType, isVideo, vUrl);

	const { fields, append, remove } = useFieldArray({
		control,
		name: 'reference',
	});

	const {
		data: cd,
		isFetching: isFetchingC,
		isFetched: isFetchedC,
	} = useQuery({
		queryKey: ['max-codenumber'],
		queryFn: () => getMaxCodeNumber(),
	});

	const { data: g } = useQuery({
		queryKey: ['project-group'],
		queryFn: () => getProductGroup({ max_size: true, order_by: 'groupname', status: 1 }),
	});

	const { data: c } = useQuery({
		queryKey: ['project-category'],
		queryFn: () => getProductCategory({ max_size: true, status: 1, order_by: 'categoryname' }),
	});

	const groups = useMemo(() => g?.data?.results || g?.data || [], [g]);
	const categories = useMemo(() => c?.data || null, [c]);

	useEffect(() => {
		if (cd?.data?.new_code_number) {
			setValue('productcodenumber', cd.data.new_code_number);
		}
	}, [cd, setValue]);

	const {
		mutate: createProject,
		isPending,
		isSuccess,
	} = usePatch({
		url: `component`,
		method: 'post',
		type: 'form-data',
		callback: (res) => {
			if (res.status === 201 && res.data?.productid) {
				toast.success(`project created successfully`);
				const { productcodenumber, productversion, productid } = res.data;
				navigate(`/${route.project}/${productcodenumber}${productversion != '-' ? '_' + productversion : ''}/${productid}`);
				return;
			}
			if (res.data.failed) {
				setFilesAttachment((prevState) => {
					const currState = prevState;
					if ('index' in res.data) {
						currState[res.data.index].error = res.data.failed;
					}
					if ('already_uploaded_files' in res.data) {
						res.data.already_uploaded_files.forEach((r) => {
							const idx = currState.findIndex((f) => f.id && f.file.name === r.file_name);
							currState[idx].error = res.data.failed;
						});
					}
					return currState;
				});
				if ('already_uploaded_files' in res.data) {
					setIsFileExist(true);
				}
			}
		},
		onError: (res) => {
			if (res?.data && Object.keys(res.data).length) {
				Object.keys(res.data).forEach((k) => {
					toast.error(res.data[k][0]);
				});
			} else {
				toast.error('something went wrong, please try again later');
			}
		},
		showError: true,
	});

	const onUploadBlob = async () => {
		const isUploadFile = filesAttachment;
		const res = await uploadBlob(isUploadFile);
		if (!res || res?.error) {
			setIsChecked(true);
			setIsLoading(false);
			return false;
		}
		return true;
	};

	const onSubmit = async (data) => {
		const isUploadFile = filesAttachment;
		if (isUploadFile.length > 0) {
			setIsLoading(true);
			if (!isChecked) {
				const isExist = await checkFileExist(isUploadFile);

				if (isExist.status) {
					setFilesAttachment((prevState) => {
						const currState = prevState;

						isExist.response.forEach((r) => {
							const idx = currState.findIndex((f) => f.id && (f.id === r.id || f.file.name === r.name));
							if (r.status === 'exist') {
								currState[idx].error = 'Eksisterende fil';
							}
						});
						return currState;
					});
					setIsFileExist(true);
					setIsLoading(false);
					setIsChecked(true);
					return;
				}
			}

			const res = await onUploadBlob();
			if (!res) return;
		}

		const d = {
			...data,
			productprefixid: data.productprefixid.id,
			projectrelations: projectRelations?.map((p) => {
				// Use selectedVersionProductId if available, otherwise fallback to productid
				const componentId = p.selectedVersionProductId || p.productid;
				return { componentcode: p.componentcode, amount: p.amount, componentid: componentId };
			}),
			status: '9',
			productcomponentflag: 0,
		};
		if (filesAttachment.length) {
			d.attachments = filesAttachment.map((a) => {
				return {
					linkdatasheet: a.linkdatasheet || a.file.name,
					privilegelevelid: a.privilegelevelid
						.filter((f) => f.id !== 99)
						.filter((p) => p.value)
						.map((p) => p.id)
						.toString()
						.trim(),
					private: a.privilegelevelid.find((p) => p.id === 99 && !!p.value) ? 1 : 0,
					cover: 1,
				};
			});
			// d.files_uploaded = filesAttachment.map((f) => f.file);
		}
		// if (data.reference?.length && data.reference.every((r) => r.reference_id)) {
		// 	d.referencerelation = data.reference.map((d) => {
		// 		return { ...d, reference_id: d.reference_id, reference_detail: d.reference_detail };
		// 	});
		// }
		if (data.reference?.length) {
			d.referencerelation = data.reference
				.filter((a) => a.reference_type && a.reference_detail)
				?.map((a) => {
					return { reference_type: a.reference_type.trim(), reference_detail: a.reference_detail.trim() };
				});
		}

		const formData = cP(d);
		await createProject(formData);
	};

	const validateFn = (file) => {
		if (!dType) return true;
		const docValidate = dType.validate;
		if (docValidate !== 0) return true;
		if (!file.includes('_')) return false;
		const name = file.substring(0, file.lastIndexOf('_'));
		const ind1 = name.substr(name.indexOf(' ') + 1);
		const fnName = ind1.substr(ind1.indexOf(' ') + 1);
		const ext = file.substring(file.lastIndexOf('.') + 1);

		const re = new RegExp('\\b' + dType.name + ' ' + number + ' ' + fnName + '_' + ver + '.' + ext + '\\b');
		return re.test(file);
	};

	const { getRootProps, getInputProps } = useDropzone({
		// accept: '*',
		multiple: true,
		onDrop: (acceptedFiles) => {
			const files = acceptedFiles.map((file, index) => {
				const id = uuidv4();
				const validate = validateFn(file.name);
				return Object.assign(
					{},
					{
						file,
						id: id,
						index: index,
						preview: URL.createObjectURL(file),
						privilegelevelid: [
							{ id: 1, name: 'Developer', value: true },
							{ id: 99, name: 'Private', value: true },
							{ id: 2, name: 'Administrasjon', value: true },
							{ id: 3, name: 'Produktsjef', value: true },
							{ id: 4, name: 'Prosjektleder', value: true },
							{ id: 5, name: 'Ansatt', value: true },
							{ id: 6, name: 'Public', value: true },
						],
						validated: validate,
					}
				);
			});
			setFilesAttachment((prevState) => [...prevState].concat(files));
		},
	});

	const onChangePrivilege = (type, event, idx, index) => {
		setFilesAttachment((prevState) => {
			let currState = prevState;
			if (type === 'delete') {
				currState = currState.toSpliced(idx, 1);
			}
			if (type === 'update') {
				currState[idx].privilegelevelid[index].value = !event.target.checked;
			}
			return currState;
		});
		setTb(!tb);
	};

	const onSaveRelation = async (data) => {
		const updatedRelations = [];

		for (const relation of data) {
			if (relation.productid) {
				try {
					const productResponse = await getProduct(relation.productid);
					let versionList = productResponse?.data?.version_list || [];

					// Ensure current product is in version_list if not already present
					const currentProductInList = versionList.some(v => v.productid === relation.productid);
					if (!currentProductInList && productResponse?.data) {
						versionList = [
							...versionList,
							{
								productid: relation.productid,
								productname: productResponse.data.productname || relation.productname,
								productversion: productResponse.data.productversion || '-',
							},
						];
					}

					// Find the highest productid (latest version) as default
					let selectedVersionProductId = relation.productid;
					if (versionList.length > 0) {
						const highestProductId = Math.max(...versionList.map(v => v.productid));
						selectedVersionProductId = highestProductId;
					}

					updatedRelations.push({
						...relation,
						version_list: versionList,
						selectedVersionProductId: selectedVersionProductId,
					});
				} catch (error) {
					// Still add the relation even if fetch fails, but without version_list
					updatedRelations.push({
						...relation,
						version_list: [],
						selectedVersionProductId: relation.productid,
					});
				}
			} else {
				// If no productid, just add the relation as is
				updatedRelations.push({
					...relation,
					selectedVersionProductId: relation.productid,
				});
			}
		}

		setProjectRelations(updatedRelations);
	};

	const updateSelectedRelation = (r, type, config) => {
		setProjectRelations((prevState) => {
			let newSelectRelations = [...prevState];
			if (type === 'delete') {
				newSelectRelations.splice(r, 1);
			}
			if (type === 'update') {
				const index = r;
				if (index !== -1) {
					newSelectRelations[index] = { ...newSelectRelations[index], ...config };
				}
			}
			return newSelectRelations;
		});
	};

	const attachments = useMemo(() => filesAttachment, [filesAttachment]);

	return (
		<>
			<BDHelmet title='Create Project' description='BDS Samferdsel create project' />
			<div className='container flex flex-col gap-8 pt-8 pb-8 mx-auto'>
				{isLoading || (isPending && !isSuccess && <Loader />)}
				<div className='flex flex-col gap-2'>
					<h1 className='text-[2.5rem] font-medium'>Lag ny</h1>
					<span>Prosjekt / Produkt / komponent</span>
					<hr />
				</div>
				<form onSubmit={handleSubmit(onSubmit)} className='flex flex-col w-full gap-4'>
					<div className='grid grid-cols-12 gap-2'>
						<BaseAutocomplete
							control={control}
							name='productprefixid'
							label='Dokumenttype'
							errors={errors}
							options={productPrefix || []}
							optionLabel='productprefix'
							config={{ classes: 'col-span-3', multiple: false, inputProps: { required: true } }}
							callback={(_, dataOption) => {
								const { id, productprefix } = dataOption;
								if (id === 12 || productprefix.includes('BD-PROJ')) {
									setValue('productcomponentflag', '2');
								}
							}}
						/>
						{isFetchingC && <LinearProgress className='col-span-2 !h-8 my-auto' />}
						{isFetchedC && (
							<BaseInputValidation
								control={control}
								name='productcodenumber'
								label='Nummer'
								config={{ type: 'number', classes: 'col-span-2' }}
								errors={errors}
								required
								disabled
							/>
						)}
						<BaseInputValidation
							control={control}
							name='productname'
							label='Produktnavn'
							config={{ classes: 'col-span-7' }}
							errors={errors}
							required
						/>
					</div>
					<div className='grid grid-cols-12 gap-2'>
						<BaseSelect
							control={control}
							name='category'
							label='Kategori'
							errors
							data={productCategory}
							config={{
								styles: {},
								classes: 'col-span-3',
								disabled: false,
								optionLabel: '--category type--',
								keyVal: 'id',
								keyName: 'categoryname',
								keyLabel: 'categoryname',
							}}
							size='small'
						/>
						{/* <BaseInputValidation
							control={control}
							name='article'
							label='Artikkel nummer'
							errors={errors}
							config={{ classes: 'col-span-9' }}
						/> */}
						<BaseInputValidation control={control} name='producturl' label='URL' errors={errors} config={{ classes: 'col-span-9' }} />
					</div>

					{/* {vUrl && !isVideo && (
						<div style={{ height: '50vh', border: '1px solid rgba(0, 0, 0, 0.3)' }}>
							<PDFViewer url={vUrl} />
						</div>
					)} */}

					{vUrl && isVideo && videoUrl && (
						<div className='flex items-center justify-center w-full h-full m-auto'>
							<ReactPlayer url={videoUrl.url} controls={true} width='50svw' height='40svh' />
						</div>
					)}
					<div className='flex gap-2'>
						<BaseInputValidation control={control} name='description' label='Beskrivelse / informasjon' errors={errors} />
					</div>
					<div className='flex justify-between gap-2'>
						<div className='relative flex flex-col gap-2 w-fit'>
							{fields.map((field, i) => {
								return (
									<Reference
										key={field.id}
										form={{ control, errors }}
										i={i}
										type='remove'
										onClick={() => {
											remove(i);
										}}
									/>
								);
							})}

							{!fields.length && (
								<Reference
									form={{ control, errors }}
									i={0}
									type='add'
									onClick={() => {
										append({ reference_type: '', reference_detail: '' });
									}}
								/>
							)}

							{fields.length > 0 && (
								<IconButton
									className='border !bg-primary !text-white !rounded-sm w-10 !absolute -right-9 top-[2px]'
									aria-label='add artikkelnummer'
									title='add artikkelnummer'
									onClick={() => {
										append({ reference_type: '', reference_detail: '' });
									}}
									type='button'
								>
									<Add />
								</IconButton>
							)}
						</div>
						<div className='flex gap-2'>
							<BaseSelect
								control={control}
								name='producttype'
								label='Type'
								errors={errors}
								data={[
									{ id: 'standard', name: 'standard', value: '0' },
									{ id: 'non-standard', name: 'non-standard', value: '1' },
									{ id: 'arkiv', name: 'arkiv', value: '2' },
								]}
								config={{ styles: {}, classes: '', disabled: false, optionLabel: '--Velg type--' }}
								size='small'
							/>

							<BaseSelect
								control={control}
								name='status'
								label='Status'
								errors
								data={projectStatus}
								config={{
									styles: {},
									classes: '!min-w-min',
									disabled: false,
									optionLabel: '--Velg status--',
									keyVal: 'id_status',
									// keyName: isProj ? 'desc' : 'label',
									keyName: 'label',
									keyLabel: 'label',
								}}
								size='small'
							/>

							<BaseInputValidation control={control} name='productversion' label='Versjon' errors={errors} />

							{/* <BaseSelect
								control={control}
								name='qrstatus'
								label='QR Link Status'
								errors={errors}
								data={[
									{ id: '0', name: 'Public', value: '0' },
									{ id: '1', name: 'Private', value: '1' },
								]}
								config={{ styles: {}, classes: '', disabled: false, optionLabel: '--Velg type--' }}
								size='small'
							/> */}
						</div>
					</div>
					<button className='mr-auto btn' onClick={() => setOpenRelation(true)} type='button'>
						Velg data til tildelte produkter / komponenter
					</button>
					{projectRelations?.length > 0 && (
						<div className='flex flex-wrap w-3/4 gap-4'>
							{projectRelations.map((r, i) => {
								const hasVersions = r.version_list && r.version_list.length > 0;
								const selectedVersionId = r.selectedVersionProductId || r.productid;

								return (
									<div
										key={`${r.productid}-${i}`}
										className='flex items-center justify-center gap-2 px-2 py-2 bg-white border border-black rounded-md'
									>
										<div className='min-w-14'>{r.productprefix}</div>
										<span>{r.productcodenumber}</span>
										<span>{r.productname}</span>
										{hasVersions && (
											<Select
												value={selectedVersionId}
												onChange={(e) => {
													updateSelectedRelation(i, 'update', { selectedVersionProductId: e.target.value });
												}}
												size='small'
												className='min-w-32'
											>
												{r.version_list.map((version) => (
													<MenuItem key={version.productid} value={version.productid}>
														{version.productversion}
													</MenuItem>
												))}
											</Select>
										)}
										<button type='button' onClick={() => updateSelectedRelation(i, 'delete')}>
											<Close fontSize='medium' sx={{ color: 'var(--primary)' }} />
										</button>
									</div>
								);
							})}
						</div>
					)}
					<div {...getRootProps({ className: 'dropzone w-fit flex flex-col gap-2 cursor-pointer' })}>
						<span>Last opp vedlegg</span>
						<div className='flex items-center gap-4'>
							<label className='flex items-center'>
								<input {...getInputProps()} />
								<button className='h-10 px-4 text-white border border-gray-700 rounded-sm bg-bd-blue' type='button'>
									<span>Velg filer</span>
								</button>
							</label>
							{filesAttachment.length > 0 ? (
								<span className='text-gray-700'>
									{filesAttachment.length} {filesAttachment.length === 1 ? 'file' : 'filer'}
								</span>
							) : (
								<span>Ingen fil valgt</span>
							)}
						</div>
					</div>

					{filesAttachment?.length > 0 && (
						<TableContainer className='w-full !max-w-[768px] xl:!w-1/2' component={Paper}>
							<Table sx={{ minWidth: 650 }} aria-label='simple table'>
								<TableHead>
									<TableRow>
										<TableCell>Filnavn</TableCell>
										<TableCell align='center'>QR kode</TableCell>
										{/* <TableCell align='center'>Administrasjon</TableCell>
										<TableCell align='center'>Produktsjef</TableCell>
										<TableCell align='center'>Prosjektleder</TableCell>
										<TableCell align='center'>Ansatt</TableCell>
										<TableCell align='center'>Public</TableCell> */}
										<TableCell align='center'>Action</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filesAttachment.map((row, idx) => (
										<TableRow
											className={twMerge(row.validated && !row.error ? 'border-2 border-green-500' : 'border-2 border-red-500')}
											key={row.name}
											sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
										>
											<TableCell component='th' scope='row' className='relative'>
												{!row.validated ? <span className='absolute text-red-500 top-1 left-4'>Wrong File Name</span> : null}
												{isLoading && (
													<CircularProgress className='h-5 w-5 absolute left-[2px] top-[30px]' sx={{ color: '#00578d' }} size={12} />
												)}
												{attachments[idx].file?.name}
											</TableCell>
											{row.privilegelevelid.map((p, i) => {
												if (p.id !== 99) return null;
												return (
													<TableCell key={[idx, i]} align='center'>
														<Checkbox
															onChange={(e) => onChangePrivilege('update', e, idx, i)}
															checked={!p.value}
															icon={<CircleOutlined />}
															checkedIcon={<Circle />}
														/>
													</TableCell>
												);
											})}

											<TableCell align='center'>
												<button type='button' onClick={(e) => onChangePrivilege('delete', e, idx)}>
													<Delete sx={{ color: 'var(--primary)' }} />
												</button>
											</TableCell>
											{row.error && (
												<span className='absolute bottom-0 text-red-500 left-4'>
													{`${row.error} ${'replace' in row ? (row.replace === 1 ? 'vil bli overskrevet' : 'vil ikke lastes opp') : ''}` ||
														'feil filnavn'}
												</span>
											)}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}

					<hr />
					<div className='flex gap-2 ml-auto'>
						<button type='button' className='btn white min-w-32' onClick={() => navigate(-1)}>
							Avbryt
						</button>
						<button type='submit' className='btn min-w-32'>
							Lagre
						</button>
					</div>
				</form>
				{openRelation && (
					<ProjectRelation
						open={!!openRelation}
						onSave={onSaveRelation}
						relations={projectRelations}
						onClose={() => setOpenRelation(false)}
						productPrefix={productPrefix}
						groups={groups}
						categories={categories}
					/>
				)}
				{isFileExist && (
					<FileExist
						open={!!isFileExist}
						onSave={(res) => {
							setFilesAttachment(res);
							setIsFileExist(false);
						}}
						files={filesAttachment}
						onClose={() => setIsFileExist(false)}
					/>
				)}
			</div>
		</>
	);
}
